import { devLog, devError } from '../utils/devLogger';
import { usePageTitle } from '../hooks/usePageTitle';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Table,
  Badge,
  Form,
  ProgressBar,
  Alert,
  Spinner
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { useAuth } from '../auth/AuthContext';
import { db } from '../firebase';
import { useUsers } from '../hooks/useUsers';
import AdminNavbar from '../components/AdminNavbar';
import FacilitatorNavbar from '../components/FacilitatorNavbar';
import '../styles/brand.css';
import AttendanceRegister from '../components/AttendanceRegister';
import { exportAttendanceToCSV } from '../utils/csvExport';

const AttendanceAnalytics = () => {
  usePageTitle("Attendance Analytics");
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [users, setUsers] = useState([]);
  const [modules, setModules] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [selectedModule, setSelectedModule] = useState('all');
  const [selectedCohort, setSelectedCohort] = useState('all');
  const [selectedDateRange, setSelectedDateRange] = useState('week');
  const [learnerStatsPage, setLearnerStatsPage] = useState(1);
  const LEARNER_PAGE_SIZE = 50;
  const { users: cachedUsers, loading: usersLoading } = useUsers();

  useEffect(() => {
    if (usersLoading) return;
    const loadData = async () => {
      try {
        setLoading(true);

        const [modulesSnapshot, attendanceSnapshot, cohortsSnapshot] = await Promise.all([
          getDocs(collection(db, 'modules')),
          getDocs(collection(db, 'attendance')),
          getDocs(collection(db, 'cohorts'))
        ]);

        const usersList = cachedUsers;

        const modulesList = modulesSnapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data()
        }));

        const attendanceList = attendanceSnapshot.docs.map((docSnap) => {
          const data = docSnap.data();
          return {
            id: docSnap.id,
            ...data,
            date: data.date?.toDate?.() || new Date(data.date || Date.now()),
            markedAt: data.markedAt?.toDate?.() || new Date(data.markedAt || Date.now())
          };
        });

        const cohortsList = cohortsSnapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data()
        }));

        setUsers(usersList);
        setModules(modulesList);
        setAttendanceRecords(attendanceList);
        setCohorts(cohortsList);
        setError(null);
      } catch (loadError) {
        setError(loadError.message || 'Failed to load attendance data.');
        // eslint-disable-next-line no-console
        devError('AttendanceAnalytics: failed to load data', loadError);
      } finally {
        setLoading(false);
      }
    };

    if (currentUser) {
      loadData();
    }
  }, [currentUser, cachedUsers, usersLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredRecords = useMemo(() => {
    let records = [...attendanceRecords];

    if (selectedModule !== 'all') {
      records = records.filter((record) => record.moduleId === selectedModule);
    }

    if (selectedCohort !== 'all') {
      records = records.filter((record) => record.cohortId === selectedCohort);
    }

    const now = new Date();
    const filterDate = new Date(now);

    switch (selectedDateRange) {
      case 'week':
        filterDate.setDate(now.getDate() - 7);
        break;
      case 'month':
        filterDate.setMonth(now.getMonth() - 1);
        break;
      case 'quarter':
        filterDate.setMonth(now.getMonth() - 3);
        break;
      default:
        filterDate.setFullYear(now.getFullYear() - 1);
    }

    records = records.filter((record) => record.date >= filterDate);

    return records;
  }, [attendanceRecords, selectedModule, selectedCohort, selectedDateRange]);

  const analytics = useMemo(() => {
    const moduleStats = {};
    const learnerStats = {};

    filteredRecords.forEach((record) => {
      // Module statistics
      if (!moduleStats[record.moduleId]) {
        moduleStats[record.moduleId] = {
          name:
            modules.find((module) => module.id === record.moduleId)?.moduleName ||
            record.moduleId,
          total: 0,
          present: 0,
          absent: 0,
          excused: 0
        };
      }
      moduleStats[record.moduleId].total += 1;
      if (record.status === 'present') moduleStats[record.moduleId].present += 1;
      if (record.status === 'absent') moduleStats[record.moduleId].absent += 1;
      if (record.status === 'excused') moduleStats[record.moduleId].excused += 1;

      // Learner statistics
      if (!learnerStats[record.learnerId]) {
        const learner = users.find((user) => user.uid === record.learnerId);
        learnerStats[record.learnerId] = {
          name: learner?.displayName || record.learnerId,
          email: learner?.email || '',
          cohortId: learner?.cohortId || record.cohortId || '',
          total: 0,
          present: 0,
          absent: 0,
          excused: 0
        };
      }
      learnerStats[record.learnerId].total += 1;
      if (record.status === 'present') learnerStats[record.learnerId].present += 1;
      if (record.status === 'absent') learnerStats[record.learnerId].absent += 1;
      if (record.status === 'excused') learnerStats[record.learnerId].excused += 1;
    });

    const totalRecords = filteredRecords.length;
    const presentRecords = filteredRecords.filter((record) => record.status === 'present').length;
    const absentRecords = filteredRecords.filter((record) => record.status === 'absent').length;
    const excusedRecords = filteredRecords.filter((record) => record.status === 'excused').length;

    const attendanceRate = totalRecords > 0 ? Math.round((presentRecords / totalRecords) * 100) : 0;
    const absenteeRate = totalRecords > 0 ? Math.round((absentRecords / totalRecords) * 100) : 0;

    const lowAttendanceAlerts = Object.values(learnerStats)
      .filter((learner) => learner.total >= 3)
      .filter((learner) => (learner.present / learner.total) * 100 < 70)
      .map((learner) => ({
        type: 'learner',
        message: `${learner.name} has low attendance (${Math.round((learner.present / learner.total) * 100)}%)`,
        severity: (learner.present / learner.total) * 100 < 50 ? 'danger' : 'warning'
      }));

    return {
      summary: {
        totalRecords,
        presentRecords,
        absentRecords,
        excusedRecords,
        attendanceRate,
        absenteeRate
      },
      moduleStats,
      learnerStats,
      lowAttendanceAlerts
    };
  }, [filteredRecords, modules, users]);

  const handleExportAttendance = () => {
    const formattedData = filteredRecords.map((record) => ({
      learnerName: users.find((user) => user.uid === record.learnerId)?.displayName || record.learnerId,
      email: users.find((user) => user.uid === record.learnerId)?.email || '',
      module: modules.find((module) => module.id === record.moduleId)?.moduleName || record.moduleId,
      date: record.date.toISOString(),
      status: record.status
    }));

    exportAttendanceToCSV(formattedData, 'attendance_analytics');
  };

  const getCohortName = (cohortId) => {
    if (!cohortId) return 'Not assigned';
    const cohort = cohorts.find((item) => item.id === cohortId);
    return cohort?.name || cohort?.cohortName || cohortId;
  };

  const renderNavbar = () => {
    if (userProfile?.role === 'admin') {
      return <AdminNavbar currentPage="attendance" />;
    }
    if (userProfile?.role === 'facilitator') {
      return <FacilitatorNavbar currentPage="attendance" />;
    }
    return null;
  };

  if (!currentUser || (userProfile?.role !== 'admin' && userProfile?.role !== 'facilitator' && userProfile?.role !== 'qa')) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">
          <h4>Access Denied</h4>
          <p>You do not have permission to access attendance analytics.</p>
          <Button
            variant="primary"
            onClick={() => {
              if (userProfile?.role === 'facilitator') {
                navigate('/facilitator');
              } else {
                navigate('/dashboard');
              }
            }}
          >
            Go to Dashboard
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <>
      {renderNavbar()}

      {/* Hero Section */}
      <section className="hero-section">
        <Container>
          <Row>
            <Col lg={8}>
              <h1 className="mb-3">Attendance Analytics</h1>
              <p className="mission-text mb-4">
                Track and analyze learner attendance patterns
              </p>
              <p className="mb-0">
                Monitor attendance trends, identify at-risk learners, and generate comprehensive reports to ensure optimal engagement across your programs.
              </p>
            </Col>
            <Col lg={4} className="d-flex align-items-center justify-content-end">
              <Button
                variant="light"
                onClick={handleExportAttendance}
              >
                Export to CSV
              </Button>
            </Col>
          </Row>
        </Container>
      </section>

      <Container fluid className="px-4 py-4">
        {userProfile?.role === 'facilitator' && (
          <Alert variant="info" className="mb-4">
            <strong>Present / Absent / Override:</strong> Use the Attendance Register below. Choose a cohort,
            pick a session, then update statuses in the <strong>Override Attendance</strong> section.
          </Alert>
        )}

        {error && <Alert variant="danger">{error}</Alert>}

        <Card className="shadow-sm border-0 mb-4">
          <Card.Body>
            <Row>
              <Col md={4} className="mb-2">
                <Form.Select value={selectedModule} onChange={(e) => setSelectedModule(e.target.value)}>
                  <option value="all">All Modules</option>
                  {modules.map((module) => (
                    <option key={module.id} value={module.id}>
                      {module.moduleName || module.title}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={4} className="mb-2">
                <Form.Select value={selectedCohort} onChange={(e) => setSelectedCohort(e.target.value)}>
                  <option value="all">All Cohorts</option>
                  {cohorts.map((cohort) => (
                    <option key={cohort.id} value={cohort.id}>
                      {cohort.name || cohort.cohortName || cohort.id}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={4} className="mb-2">
                <Form.Select
                  value={selectedDateRange}
                  onChange={(e) => setSelectedDateRange(e.target.value)}
                >
                  <option value="week">Last 7 days</option>
                  <option value="month">Last 30 days</option>
                  <option value="quarter">Last 90 days</option>
                  <option value="year">Last Year</option>
                </Form.Select>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {loading ? (
          <Card className="shadow-sm border-0">
            <Card.Body className="text-center text-muted py-5">
              <Spinner animation="border" size="sm" className="me-2" />
              Loading attendance analytics...
            </Card.Body>
          </Card>
        ) : (
          <>
            <Row>
              <Col md={4} className="mb-3">
                <Card className="h-100 shadow-sm border-0">
                  <Card.Body>
                    <h6 className="text-muted mb-2">Attendance Rate</h6>
                    <h3>{analytics.summary.attendanceRate}%</h3>
                    <ProgressBar now={analytics.summary.attendanceRate} variant="success" />
                    <small className="text-muted">
                      Present: {analytics.summary.presentRecords} / Total: {analytics.summary.totalRecords}
                    </small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4} className="mb-3">
                <Card className="h-100 shadow-sm border-0">
                  <Card.Body>
                    <h6 className="text-muted mb-2">Absentee Rate</h6>
                    <h3>{analytics.summary.absenteeRate}%</h3>
                    <ProgressBar now={analytics.summary.absenteeRate} variant="danger" />
                    <small className="text-muted">
                      Absent: {analytics.summary.absentRecords} / Total: {analytics.summary.totalRecords}
                    </small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4} className="mb-3">
                <Card className="h-100 shadow-sm border-0">
                  <Card.Body>
                    <h6 className="text-muted mb-2">Excused Absences</h6>
                    <h3>{analytics.summary.excusedRecords}</h3>
                    <ProgressBar now={analytics.summary.excusedRecords} variant="info" />
                    <small className="text-muted">Excused entries in selected period</small>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {analytics.lowAttendanceAlerts.length > 0 && (
              <Alert variant="warning">
                <Alert.Heading>Low Attendance Alerts</Alert.Heading>
                <ul className="mb-0">
                  {analytics.lowAttendanceAlerts.map((alertItem, index) => (
                    <li key={`alert-${index}`}>{alertItem.message}</li>
                  ))}
                </ul>
              </Alert>
            )}

            <Row className="mt-4">
              <Col lg={6} className="mb-3">
                <Card className="shadow-sm border-0 h-100">
                  <Card.Header className="bg-white">
                    <h5 className="mb-0">Attendance by Module</h5>
                  </Card.Header>
                  <Card.Body>
                    <Table striped bordered hover responsive>
                      <thead>
                        <tr>
                          <th>Module</th>
                          <th>Total</th>
                          <th>Present</th>
                          <th>Rate</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.values(analytics.moduleStats).map((moduleStat) => {
                          const attendanceRate = moduleStat.total > 0
                            ? Math.round((moduleStat.present / moduleStat.total) * 100)
                            : 0;
                          return (
                            <tr key={moduleStat.name}>
                              <td>{moduleStat.name}</td>
                              <td>{moduleStat.total}</td>
                              <td>{moduleStat.present}</td>
                              <td>
                                <Badge bg={attendanceRate >= 75 ? 'success' : 'warning'}>
                                  {attendanceRate}%
                                </Badge>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>
              <Col lg={6} className="mb-3">
                <Card className="shadow-sm border-0 h-100">
                  <Card.Header className="bg-white">
                    <h5 className="mb-0">Attendance by Learner</h5>
                  </Card.Header>
                  <Card.Body>
                    <div style={{ maxHeight: '420px', overflowY: 'auto' }}>
                      <Table striped bordered hover responsive className="mb-0">
                      <thead>
                        <tr>
                          <th>Learner</th>
                          <th>Cohort</th>
                          <th>Total</th>
                          <th>Present</th>
                          <th>Rate</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(analytics.learnerStats).slice((learnerStatsPage - 1) * LEARNER_PAGE_SIZE, learnerStatsPage * LEARNER_PAGE_SIZE).map(([learnerId, learner]) => {
                          const attendanceRate = learner.total > 0
                            ? Math.round((learner.present / learner.total) * 100)
                            : 0;
                          return (
                            <tr key={learnerId}>
                              <td>
                                <div>{learner.name}</div>
                                <small className="text-muted">{learner.email}</small>
                              </td>
                              <td>{getCohortName(learner.cohortId)}</td>
                              <td>{learner.total}</td>
                              <td>{learner.present}</td>
                              <td>
                                <Badge bg={attendanceRate >= 75 ? 'success' : 'warning'}>
                                  {attendanceRate}%
                                </Badge>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      </Table>
                      {Object.keys(analytics.learnerStats).length > LEARNER_PAGE_SIZE && (
                        <div className="d-flex justify-content-between align-items-center mt-3">
                          <small className="text-muted">Showing {Math.min(learnerStatsPage * LEARNER_PAGE_SIZE, Object.keys(analytics.learnerStats).length)} of {Object.keys(analytics.learnerStats).length} learners</small>
                          <div className="d-flex gap-2">
                            <Button size="sm" variant="outline-secondary" disabled={learnerStatsPage === 1} onClick={() => setLearnerStatsPage(p => p - 1)}>Previous</Button>
                            <Button size="sm" variant="outline-secondary" disabled={learnerStatsPage * LEARNER_PAGE_SIZE >= Object.keys(analytics.learnerStats).length} onClick={() => setLearnerStatsPage(p => p + 1)}>Next</Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </>
        )}

        <Row className="mt-4">
          <Col>
            <Card className="mb-3">
              <Card.Header>
                <h5 className="mb-0">Request Attendance Confirmation</h5>
              </Card.Header>
              <Card.Body>
                <p className="text-muted mb-3">
                  Create a session and prompt learners to confirm their attendance after the session ends.
                </p>
                <AttendanceRegister
                  modules={modules}
                  facilitatorMode={userProfile?.role === 'facilitator'}
                  initialViewMode="single"
                />
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default AttendanceAnalytics;
